import React, { useState, useEffect, useMemo } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { allProducts, categoryList } from "../data/index";
import "./Products.css";

const PAGE_SIZE = 12;

const Products = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const categoryParam = searchParams.get("category") || "All";
  const [activeCategory, setActiveCategory] = useState(categoryParam);
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);

  useEffect(() => {
    setActiveCategory(categoryParam);
    setVisibleCount(PAGE_SIZE);
  }, [categoryParam]);

  const handleCategoryChange = (catName) => {
    setActiveCategory(catName);
    setVisibleCount(PAGE_SIZE);
    catName === "All"
      ? setSearchParams({})
      : setSearchParams({ category: catName });
    window.scrollTo({ top: 300, behavior: "smooth" });
  };

  const filteredProducts = useMemo(() => {
    const base =
      activeCategory === "All"
        ? allProducts
        : allProducts.filter(
            (p) => p.category.toLowerCase() === activeCategory.toLowerCase(),
          );
    const a = [...base];
    for (let i = a.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [a[i], a[j]] = [a[j], a[i]];
    }
    return a;
    // eslint-disable-next-line
  }, [activeCategory]);

  const visibleProducts = filteredProducts.slice(0, visibleCount);
  const hasMore = visibleCount < filteredProducts.length;
  const currentCat = categoryList.find(
    (c) => c.name.toLowerCase() === activeCategory.toLowerCase(),
  );
  const totalCount =
    activeCategory === "All"
      ? allProducts.length
      : currentCat
        ? currentCat.count
        : 0;

  // ← Dynamic SEO based on active category
  const pageTitle =
    activeCategory === "All"
      ? "Products | RK Overseas — Handcrafted Glass Decorative Items, India"
      : `${activeCategory} | RK Overseas — Glass Manufacturer Firozabad, India`;

  const pageDescription =
    activeCategory === "All"
      ? `Explore ${allProducts.length}+ handcrafted glass decorative products by RK Overseas, Firozabad, India. Candle holders, vases, votives, Christmas ornaments and more. Worldwide export available.`
      : `Browse our ${activeCategory} collection — ${totalCount} handcrafted glass products by RK Overseas, Firozabad, India. Premium quality, worldwide export available.`;

  // ← Cloudinary optimization helper
  const optimizeUrl = (url) => {
    if (!url || !url.includes("res.cloudinary.com")) return url;
    return url.replace("/upload/", "/upload/q_auto,f_auto,/");
  };

  return (
    <div className="products-page">
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDescription} />
        <meta
          name="keywords"
          content={`${activeCategory === "All" ? "glass decorative items" : activeCategory.toLowerCase()}, glass manufacturer firozabad, handcrafted glass india, glass exporter india, RK Overseas`}
        />
        <link
          rel="canonical"
          href={`https://rkoverseasindia.com/product${activeCategory !== "All" ? `?category=${encodeURIComponent(activeCategory)}` : ""}`}
        />
      </Helmet>

      <div className="page-banner">
        <h1>Our Products</h1>
        <p className="breadcrumb">Home / Products</p>
      </div>

      <section className="section">
        <div className="container">
          <div className="products-page__topbar">
            <div className="products-page__topbar-left">
              <h2 className="products-page__heading">
                {activeCategory === "All" ? "All Products" : activeCategory}
              </h2>
              <span className="products-page__count">{totalCount} items</span>
            </div>
            <div className="products-page__dropdown-wrap">
              <label htmlFor="cat-select">Browse by Category</label>
              <select
                id="cat-select"
                className="products-page__dropdown"
                value={activeCategory}
                onChange={(e) => handleCategoryChange(e.target.value)}
              >
                <option value="All">
                  All Categories ({allProducts.length})
                </option>
                {categoryList.map((cat) => (
                  <option key={cat.name} value={cat.name}>
                    {cat.name} ({cat.count})
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="products-page__pills">
            <button
              className={
                "products-page__pill" +
                (activeCategory === "All" ? " active" : "")
              }
              onClick={() => handleCategoryChange("All")}
            >
              All
            </button>
            {categoryList.map((cat) => (
              <button
                key={cat.name}
                className={
                  "products-page__pill" +
                  (activeCategory === cat.name ? " active" : "")
                }
                onClick={() => handleCategoryChange(cat.name)}
              >
                {cat.name}{" "}
                <span className="products-page__pill-count">{cat.count}</span>
              </button>
            ))}
          </div>

          <div className="products-page__grid">
            {visibleProducts.map((product) => (
              <Link
                to={"/product/" + product.globalId}
                className="products-page__card"
                key={product.globalId}
                aria-label={`View details of ${product.name}`}
              >
                <div className="products-page__img-wrap">
                  <img
                    src={optimizeUrl(product.imageUrl)}
                    alt={product.name}
                    loading="lazy"
                  />
                  <div className="products-page__hover">
                    <span>View Details</span>
                  </div>
                </div>
                <div className="products-page__info">
                  <h3>{product.name}</h3>
                  <p className="products-page__cat">{product.category}</p>
                </div>
              </Link>
            ))}
          </div>

          {hasMore && (
            <div className="products-page__showmore">
              <p className="products-page__showing">
                Showing {visibleCount} of {filteredProducts.length} products
              </p>
              <button
                className="btn-primary"
                onClick={() => setVisibleCount((v) => v + PAGE_SIZE)}
              >
                Show More
              </button>
            </div>
          )}
          {!hasMore && filteredProducts.length > 0 && (
            <div className="products-page__showmore">
              <p className="products-page__showing">
                All {filteredProducts.length} products shown
              </p>
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default Products;
